import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { healthcareInvoicesApi } from '../services/api';
import './clinic.css';
import './Invoices.css';
import InvoicePrintDetails from './InvoicePrintDetails';

const statusLabels = {
  draft: 'Bozza',
  issued: 'Emessa'
};

const tsLabels = {
  not_ready: 'Da preparare',
  awaiting_payment: 'Attende saldo',
  pending: 'Da inviare',
  validating: 'Verifica in corso',
  validated: 'Validata',
  validation_error: 'Errore TS',
  sending: 'Invio in corso',
  uncertain: 'Da controllare',
  submitted: 'Inviata'
};

const paymentLabels = {
  cash: 'Contanti',
  card: 'Carta/POS',
  transfer: 'Bonifico',
  other_tracked: 'Altro tracciabile'
};

const money = value => Number(value || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const dateOnly = value => value ? new Date(`${value}T12:00:00`).toLocaleDateString('it-IT') : 'Da registrare';
const invoiceNumber = invoice => invoice.documentNumber || (invoice.number ? `${invoice.series}/${invoice.number}/${invoice.year}` : 'BOZZA');
const canDownload = invoice => invoice?.id;

async function download(blobPromise, filename) {
  const blob = await blobPromise;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function invoiceTone(invoice) {
  if (invoice.status === 'draft') return 'draft';
  if (!invoice.paidOn) return 'open';
  if (invoice.tsState === 'submitted') return 'closed';
  if (invoice.tsState === 'validation_error' || invoice.tsState === 'uncertain') return 'attention';
  return 'open';
}

function invoiceStatus(invoice) {
  if (invoice.status === 'draft') return 'Da emettere';
  if (!invoice.paidOn) return 'Saldo da registrare';
  if (invoice.tsState === 'submitted') return 'Emessa · TS inviato';
  return tsLabels[invoice.tsState] || invoice.tsState || 'TS da controllare';
}

function FiscalSettings({ settings, busy, onChange, onSave, savedMessage, onLogo }) {
  if (!settings) return <section className="invoice-settings-card"><p>Caricamento impostazioni fiscali…</p></section>;
  return <section className="invoice-settings-card" aria-labelledby="invoice-settings-title">
    <div>
      <h2 id="invoice-settings-title">Stile e diciture</h2>
      <p>Queste impostazioni valgono per le prossime bozze e per le fatture emesse dopo il salvataggio.</p>
    </div>
    <div className="invoice-template-card">
      <span>Logo del professionista</span>
      {settings.logoDataUrl && <img className="invoice-studio-logo" src={settings.logoDataUrl} alt="Logo del professionista" />}
      <label className="invoice-logo-upload">Carica logo PNG o JPEG
        <input type="file" accept="image/png,image/jpeg" onChange={onLogo} disabled={busy}/>
      </label>
      <small>Massimo 500 KB. Salva le impostazioni per applicarlo ai PDF.</small>
      {settings.logoDataUrl && <button type="button" className="invoice-action" disabled={busy} onClick={() => onChange({target:{name:'logoDataUrl',value:''}})}>Rimuovi logo</button>}
      <span>Template PDF attivo</span>
      <strong>Sibilla verde · sanitario</strong>
      <p>È il modello grafico usato per il PDF da consegnare o stampare. Intestazione studio, importi, bollo e note fiscali vengono composti in automatico.</p>
    </div>
    <form className="invoice-settings-form" onSubmit={onSave}>
      <label>Dicitura fiscale in fattura
        <textarea name="taxDescription" rows="3" maxLength="300" value={settings.taxDescription || ''} onChange={onChange} required />
      </label>
      <div className="invoice-settings-grid">
        <label>IVA
          <select name="vatNature" value={settings.vatNature || ''} onChange={onChange} required>
            <option value="">Seleziona</option>
            <option value="N4">Esente IVA — prestazione sanitaria</option>
            <option value="N2.2">Non soggetta — regime forfettario</option>
          </select>
        </label>
        <label>Sezionale
          <input name="series" value={settings.series || ''} maxLength="5" onChange={onChange} required />
        </label>
      </div>
      <p>La descrizione della singola fattura si modifica con “Modifica dettagli” nell’archivio o nella visita.</p>
      <div className="invoice-settings-actions">
        <label className="invoice-confirm"><input type="checkbox" name="confirmed" checked={!!settings.confirmed} onChange={onChange} /> Dati fiscali verificati</label>
        <button className="clinic-button" disabled={busy}>{busy ? 'Salvataggio…' : 'Salva impostazioni'}</button>
      </div>
      {savedMessage && <strong className="invoice-saved" role="status">{savedMessage}</strong>}
    </form>
  </section>;
}

export function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [year, setYear] = useState(String(new Date().getFullYear()));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [invoiceData, settingsData] = await Promise.all([healthcareInvoicesApi.list(), healthcareInvoicesApi.settings()]);
      setInvoices(invoiceData || []);
      setSettings(settingsData.settings || null);
      if (settingsData.settings?.year) setYear(String(settingsData.settings.year));
    } catch (requestError) {
      setError(requestError.message || 'Non è stato possibile caricare le fatture.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    const issued = invoices.filter(invoice => invoice.status === 'issued');
    return {
      issued: issued.length,
      drafts: invoices.filter(invoice => invoice.status === 'draft').length,
      unpaid: issued.filter(invoice => !invoice.paidOn).length,
      tsOpen: issued.filter(invoice => invoice.paidOn && invoice.tsState !== 'submitted').length,
      total: issued.reduce((sum, invoice) => sum + Number(invoice.total || 0), 0)
    };
  }, [invoices]);

  const changeSetting = event => {
    const { name, value, type, checked } = event.target;
    setMessage('');
    setSettings(current => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const changeLogo = async event => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError('');
    if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > 512000) {
      setError('Scegli un logo PNG o JPEG fino a 500 KB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { setSettings(current => ({ ...current, logoDataUrl: reader.result })); setMessage(''); };
    reader.onerror = () => setError('Impossibile leggere il logo. Riprova con un altro file.');
    reader.readAsDataURL(file);
  };

  const saveSettings = async event => {
    event.preventDefault();
    if (!settings) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await healthcareInvoicesApi.saveSettings({
        ...settings,
        year: Number(settings.year),
        firstNumber: Number(settings.firstNumber),
        pensionPercent: Number(settings.pensionPercent),
        stampAuthorizationDate: settings.stampAuthorizationDate || null
      });
      setSettings(saved);
      setMessage('Dicitura aggiornata per le prossime fatture.');
    } catch (requestError) {
      setError(requestError.message || 'Impostazioni non salvate.');
    } finally {
      setBusy(false);
    }
  };

  const run = async action => {
    setBusy(true);
    setError('');
    try { await action(); }
    catch (requestError) { setError(requestError.message || 'Operazione non riuscita.'); }
    finally { setBusy(false); }
  };

  return <main className="clinic-page invoices-page" aria-busy={loading || busy}>
    <header className="invoices-hero">
      <div>
        <h1>Fatture</h1>
        <p>Consulta le fatture, scarica i PDF e controlla pagamenti e invii TS.</p>
      </div>
      <button className="clinic-button secondary" disabled={loading || busy} onClick={load}>{loading ? 'Aggiornamento…' : 'Aggiorna'}</button>
    </header>

    {error && <div className="clinic-alert error" role="alert">{error}</div>}

    <section className="invoice-summary" aria-label="Riepilogo fatture">
      <div><span>Emesse</span><strong>{summary.issued}</strong></div>
      <div><span>Bozze</span><strong>{summary.drafts}</strong></div>
      <div><span>Saldo aperto</span><strong>{summary.unpaid}</strong></div>
      <div><span>TS da controllare</span><strong>{summary.tsOpen}</strong></div>
      <div><span>Totale emesso</span><strong>{money(summary.total)}</strong></div>
    </section>

    <section className="invoice-archive" aria-labelledby="invoice-archive-title">
      <div className="invoice-archive-head">
        <div><h2 id="invoice-archive-title">Archivio documenti</h2><p>{invoices.length ? `${invoices.length} documenti fiscali salvati` : 'Nessuna fattura salvata nello studio.'}</p></div>
        <div className="invoice-register-actions">
          <label>Anno registro
            <input type="number" min="2000" max="2100" value={year} onChange={event => setYear(event.target.value)} />
          </label>
          <button className="clinic-button secondary" disabled={busy || !year} onClick={() => run(() => download(healthcareInvoicesApi.register(year), `registro-fatture-${year}.csv`))}>Esporta registro CSV</button>
        </div>
      </div>
      {loading ? <div className="clinic-empty" role="status">Caricamento fatture…</div> : invoices.length ? <div className="invoice-table" role="table" aria-label="Fatture emesse e bozze">
        <div className="invoice-row invoice-row-head" role="row">
          <span>Documento</span><span>Paziente</span><span>Importo</span><span>Stato</span><span>Azioni</span>
        </div>
        {invoices.map(invoice => <article className={`invoice-row tone-${invoiceTone(invoice)}`} role="row" key={invoice.id}>
          <div role="cell"><strong>{invoiceNumber(invoice)}</strong><small>{statusLabels[invoice.status] || invoice.status} · {dateOnly(invoice.issueDate)}</small></div>
          <div role="cell"><strong>{invoice.buyerName || 'Paziente'}</strong><small>{invoice.description || 'Prestazione sanitaria'}</small></div>
          <div role="cell"><strong>{money(invoice.total)}</strong><small>{invoice.paidOn ? `${paymentLabels[invoice.paymentMethod] || invoice.paymentMethod} · ${dateOnly(invoice.paidOn)}` : 'Saldo non registrato'}</small></div>
          <div role="cell"><span className="invoice-status">{invoiceStatus(invoice)}</span>{invoice.tsState !== 'submitted' && <small>{tsLabels[invoice.tsState] || invoice.tsState}</small>}
            {invoice.status === 'issued' && invoice.stampMode === 'unverified' && <small>Nota: bollo da verificare</small>}
            {invoice.status === 'issued' && (!invoice.buyerAddress || invoice.buyerAddress.trim().length < 6) && <small>Nota: indirizzo da completare</small>}
          </div>
          <div className="invoice-actions" role="cell">
            <Link className="invoice-action" to={`/patients/${invoice.patientId}/visits/${invoice.visitId}`}>Apri visita</Link>
            {canDownload(invoice) && <button type="button" className="invoice-action" disabled={busy} onClick={() => run(() => download(healthcareInvoicesApi.pdf(invoice.id), `${invoice.status === 'issued' ? 'fattura' : 'bozza'}-${invoiceNumber(invoice).replaceAll('/', '-')}.pdf`))}>PDF</button>}
          </div>
          <div className="invoice-details-row"><InvoicePrintDetails invoice={invoice} onSaved={saved => setInvoices(items => items.map(item => item.id === saved.id ? saved : item))}/></div>
        </article>)}
      </div> : <div className="clinic-empty">Quando emetti una fattura dalla visita, comparirà qui con PDF, saldo e stato TS.</div>}
    </section>
    <details className="invoice-preferences">
      <summary>Impostazioni fattura <span>Modello PDF e diciture</span></summary>
      <FiscalSettings settings={settings} busy={busy} onChange={changeSetting} onSave={saveSettings} savedMessage={message} onLogo={changeLogo}/>
    </details>
  </main>;
}

export default Invoices;
